import { refreshSubscriptionQueries } from '@/lib/subscription-query-keys';
/**
 * Modal de troca de plano aguardando pagamento (exige ação)
 *
 * Exibido quando `GET /billing/my-subscription` devolve `pending_plan_id`: o
 * usuário pediu um upgrade/troca para plano pago — logo após confirmar a troca
 * em Minha Conta, ou ao voltar sem ter concluído o pagamento. O plano vigente
 * permanece o antigo — este
 * modal informa a cobrança em aberto e oferece as duas saídas do chamado:
 * "Realize o pagamento" (abre o checkout em nova aba, preservando a sessão) ou
 * continuar no plano atual (descarta a troca pendente no backend).
 *
 * Não-dismissível por X/ESC/clique-fora (padrão do TrialEndedModal): enquanto
 * a pendência existir no backend, o modal reaparece a cada acesso.
 *
 * No período de teste, a pendência do PRÓPRIO plano é a assinatura pedida
 * (chamado 7176): o texto fala em assinatura e a saída é continuar no teste.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCard, ArrowRight } from "lucide-react";
import { accountService, type UserSubscription } from "@/services/account.service";
import { payPendingCharge } from "@/lib/pending-charge";
import { pendingPlanChangeChargeLine, pendingPlanChangeDescription } from "@/lib/plan-change-messages";
import { TRIAL_PENDING_COPY, isTrialConversionPending, trialExitLabel } from "@/lib/trial-subscription";

interface PendingPlanChangeModalProps {
  open: boolean;
  subscription: UserSubscription;
  onClose: () => void;
}

export function PendingPlanChangeModal({
  open,
  subscription,
  onClose,
}: Readonly<PendingPlanChangeModalProps>) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cicloAnual = subscription.pending_billing_cycle === "yearly";
  const assinaturaNoTeste = isTrialConversionPending(subscription);
  const titulo = assinaturaNoTeste
    ? TRIAL_PENDING_COPY.title
    : "Sua troca de plano aguarda pagamento";
  const descricao = assinaturaNoTeste
    ? TRIAL_PENDING_COPY.description
    : pendingPlanChangeDescription(subscription.pending_plan_name, subscription.plan_name);
  const rotuloDaSaida = assinaturaNoTeste ? trialExitLabel(subscription) : "Continuar no plano atual";

  const handlePagar = () => {
    payPendingCharge(subscription.pending_checkout_url, navigate);
    onClose();
  };

  const handleContinuarNoPlanoAtual = async () => {
    setCancelling(true);
    setError(null);
    try {
      await accountService.cancelPendingPlanChange();
      await refreshSubscriptionQueries(queryClient);
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail;
      setError(
        detail || "Não foi possível cancelar a troca de plano. Tente novamente.",
      );
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open}>
      {/* Não-dismissível: só fecha por uma das duas ações. */}
      <DialogContent
        className="sm:max-w-lg text-center [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <CreditCard className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <DialogTitle className="text-2xl font-bold">{titulo}</DialogTitle>
          <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
            {pendingPlanChangeChargeLine(subscription.pending_plan_name, cicloAnual)}
          </p>
          <DialogDescription className="text-base">{descricao}</DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-3">
          <Button
            className="w-full py-6 text-base font-semibold"
            onClick={handlePagar}
            disabled={cancelling}
          >
            Realize o pagamento
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <Button
            variant="outline"
            className="w-full py-5"
            onClick={handleContinuarNoPlanoAtual}
            disabled={cancelling}
          >
            {cancelling ? "Cancelando..." : rotuloDaSaida}
          </Button>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PendingPlanChangeModal;
